import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createComment, createCommentReply } from "../services/postServices";
import { FEED_QUERY_KEYS } from "../queryKeys";

export const useCreateCommentMutation = ({ postId }: { postId?: number }) => {
  const queryClient = useQueryClient();

  const commentMutation = useMutation({
    mutationFn: (text: string) => {
      if (!postId) throw new Error("postId is required for creating a comment");
      return createComment({ postId, text });
    },
    onSuccess: () => {
      if (postId) {
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.postData(postId) });
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.comments(postId) });
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.commentsList() });
      }
    },
  });

  const replyMutation = useMutation({
    mutationFn: async ({ commentId, text }: { commentId: number; text: string }) => {
      if (!postId) throw new Error("postId is required for creating a comment reply");
      return createCommentReply(commentId, { text });
    },
    onSuccess: (_, { commentId }) => {
      if (postId) {
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.postData(postId) });
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.comments(postId) });
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.commentsList() });
      }
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.commentData(commentId) });
    },
  });

  return {
    createComment: commentMutation.mutate,
    isCreatingComment: commentMutation.isPending,
    createReply: replyMutation.mutate,
    isCreatingReply: replyMutation.isPending,
  };
};
